import type { SyncBlockError, SyncBlockInstance } from '@atlaskit/editor-synced-block-provider';
import { fetchReferences, getConfluencePageAri } from '@atlaskit/editor-synced-block-provider';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';

const SYNC_BLOCK_PRELOAD_TIMEOUT = 500; // in milliseconds
const SSR_NODE_NAMESPACE = '__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__';

const getContentIdAndTypeFromConfluenceViewPageUrl = (url: string) => {
	// Match page URLs: /spaces/SPACE/pages/123456/...
	const pageIdMatch = url.match(/\/pages\/(\d+)/);

	// Match blog URLs: /spaces/SPACE/blog/YYYY/MM/DD/123456789
	const blogPostMatch = url.match(/\/blog\/\d{4}\/\d{2}\/\d{2}\/(\d+)/);

	if (pageIdMatch && pageIdMatch[1]) {
		return { contentId: pageIdMatch[1], type: 'page' as const };
	} else if (blogPostMatch && blogPostMatch[1]) {
		return { contentId: blogPostMatch[1], type: 'blogpost' as const };
	}

	return null;
};

export const storeSyncBlockData = (syncBlockData: SyncBlockInstance[]): void => {
	if (!window[SSR_NODE_NAMESPACE]) {
		window[SSR_NODE_NAMESPACE] = {} as { [key: string]: any };
	}

	const existingData = (window[SSR_NODE_NAMESPACE] as { [key: string]: any })['syncBlocks'] || [];

	// Merge existing data with new data and ensure unique by contentId
	// incase of multiple renderers on the same page
	const mergedMap = new Map<string, SyncBlockInstance>();

	// Add existing data to map
	if (Array.isArray(existingData)) {
		existingData.forEach((syncBlockInstance: SyncBlockInstance) => {
			if (syncBlockInstance.resourceId) {
				mergedMap.set(syncBlockInstance.resourceId, syncBlockInstance);
			}
		});
	}

	// Add new data to map (overwriting duplicates by id)
	syncBlockData.forEach((syncBlockInstance: SyncBlockInstance) => {
		if (syncBlockInstance.resourceId) {
			mergedMap.set(syncBlockInstance.resourceId, syncBlockInstance);
		}
	});

	(window[SSR_NODE_NAMESPACE] as { [key: string]: any })['syncBlocks'] = Array.from(
		mergedMap.values(),
	);
};

export const getPageAri = (): string | null => {
	const { uri, cloudId } = getSSRRenderInputs();

	const contentInfo = getContentIdAndTypeFromConfluenceViewPageUrl(uri);

	if (!contentInfo) {
		return null;
	}

	const { contentId, type: contentType } = contentInfo;
	return getConfluencePageAri({ pageId: contentId, cloudId, pageType: contentType });
};

export const preloadSyncBlockData = async (): Promise<void> => {
	await SSRMeasures.run('ssr-app/prepare/preloadQuery/syncBlock', async () => {
		if (!expValEquals('platform_synced_block', 'isEnabled', true)) {
			return;
		}

		try {
			const documentAri = getPageAri();

			if (!documentAri) {
				return;
			}

			// Fetch sync block content with timeout
			const syncBlockData: SyncBlockInstance[] | null = await timeGuard(
				async () => {
					const results: SyncBlockInstance[] | SyncBlockError = await fetchReferences(documentAri);

					if (!results || typeof results === 'string') {
						ssrPrepareErrorsClient.submitError(
							new Error(`Failed to fetch sync block references: ${results || 'unknown error'}`),
						);
						return null;
					}

					return results as SyncBlockInstance[];
				},
				() => {
					ssrPrepareErrorsClient.submitError(
						new Error(
							`Sync block preload timed out after ${SYNC_BLOCK_PRELOAD_TIMEOUT}ms. sync blocks will be loaded client-side.`,
						),
					);
					return null;
				},
				SYNC_BLOCK_PRELOAD_TIMEOUT,
			);

			// Append documentAri to each sync block and store
			if (syncBlockData && Object.keys(syncBlockData).length > 0) {
				storeSyncBlockData(syncBlockData);
			}
		} catch (error) {
			// Log error but don't fail the entire SSR process
			ssrPrepareErrorsClient.submitError(
				new Error(
					`Unexpected error during sync block preload: ${error instanceof Error ? error.message : String(error)}`,
				),
			);
		}
	});
};
